import { CancelRounded, CheckCircleRounded } from '@mui/icons-material';
import {
  Box,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

const OtherTypography = ({ children }) => {
  return (
    <Typography
      variant="body1"
      sx={{
        fontSize: '16px',
        fontWeight: '400',
        color: 'primary.neutral500',
      }}
    >
      {children}
    </Typography>
  );
};

const SlexTypography = ({ children }) => {
  return (
    <Typography
      variant="body1"
      sx={{
        fontSize: '17px',
        fontWeight: '500',
        color: 'primary.neutral800',
      }}
    >
      {children}
    </Typography>
  );
};

export default function Section7() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const features = [
    {
      title: t('HOME_PAGE.SECTION7.TITLE'),
      other: (
        <Box
          sx={{
            bgcolor: { xs: 'transparent', sm: 'primary.neutral200' },
            py: 1.2,
            px: { xs: 0, sm: 2 },
            borderRadius: theme.shape.defaultBorderRadius,
            fontWeight: '500',
            color: 'primary.neutral800',
            textAlign: 'center',
            width: { xs: '100%', lg: '70%' },
            mx: 'auto',
            fontSize: { xs: '14px', lg: '16px' },
          }}
        >
          {t('HOME_PAGE.SECTION7.OTHER_PROVIDERS')}
        </Box>
      ),
      slex: (
        <Box
          sx={{
            bgcolor: 'primary.main',
            py: 1.2,
            px: 2,
            borderRadius: theme.shape.defaultBorderRadius,
            fontWeight: '500',
            color: 'white',
            textAlign: 'center',
            width: { xs: '100%', lg: '70%' },
            mx: 'auto',
            fontSize: { xs: '14px', lg: '16px' },
          }}
        >
          Slex
        </Box>
      ),
    },
    {
      other: <OtherTypography>8,000+</OtherTypography>,
      slex: <SlexTypography>35,000+</SlexTypography>,
      title: t('HOME_PAGE.SECTION7.FEATURES.0.TITLE'),
      description: t('HOME_PAGE.SECTION7.FEATURES.0.DESCRIPTION'),
    },
    {
      other: <OtherTypography>30,000+</OtherTypography>,
      slex: <SlexTypography>70,000+</SlexTypography>,
      title: t('HOME_PAGE.SECTION7.FEATURES.1.TITLE'),
      description: t('HOME_PAGE.SECTION7.FEATURES.1.DESCRIPTION'),
    },
    {
      other: (
        <CancelRounded
          sx={{
            color: 'primary.neutral500',
          }}
        />
      ),
      slex: (
        <CheckCircleRounded
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      ),
      title: t('HOME_PAGE.SECTION7.FEATURES.2.TITLE'),
      description: t('HOME_PAGE.SECTION7.FEATURES.2.DESCRIPTION'),
    },
    {
      other: <OtherTypography>LIMITED</OtherTypography>,
      title: t('HOME_PAGE.SECTION7.FEATURES.3.TITLE'),
      description: t('HOME_PAGE.SECTION7.FEATURES.3.DESCRIPTION'),
      slex: (
        <CheckCircleRounded
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      ),
    },
    {
      other: <OtherTypography>LIMITED</OtherTypography>,
      slex: (
        <CheckCircleRounded
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      ),
      title: t('HOME_PAGE.SECTION7.FEATURES.4.TITLE'),
      description: t('HOME_PAGE.SECTION7.FEATURES.4.DESCRIPTION'),
    },
    {
      other: <OtherTypography>-</OtherTypography>,
      slex: (
        <CheckCircleRounded
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      ),
      title: t('HOME_PAGE.SECTION7.FEATURES.5.TITLE'),
      description: t('HOME_PAGE.SECTION7.FEATURES.5.DESCRIPTION'),
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.primary.primarylight3, 0.4),
        my: { xs: 6, md: 8 },
      }}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          py: { xs: 3, md: 4 },
          gap: '80px',
          alignItems: 'center',
        }}
        maxWidth={'lg'}
      >
        <Stack
          direction={'column'}
          sx={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}
        >
          {features.map((feature, index) => (
            <Fragment key={index}>
              <Grid
                container
                gridTemplateColumns={'repeat(3, 1fr)'}
                gap={'20px'}
                alignItems={'center'}
                sx={{ py: '20px' }}
              >
                <Grid item xl lg md sm xs>
                  {index === 0 ? (
                    <Stack component={'header'}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { xs: '18px', md: '28px', lg: '32px' },
                          fontWeight: '600',
                          color: 'primary.neutral800',
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: '14px', sm: '18px' },
                        fontWeight: '500',
                        color: 'primary.neutral800',
                      }}
                    >
                      {feature.title}
                    </Typography>
                  )}
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'primary.neutral500',
                      fontSize: { xs: '14px', sm: '16px' },
                      fontWeight: '300',
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Grid>
                <Grid item xl lg md sm xs sx={{ textAlign: 'center' }}>
                  {feature.other}
                </Grid>
                <Grid item xl lg md sm xs sx={{ textAlign: 'center' }}>
                  {feature.slex}
                </Grid>
              </Grid>
              {index === 0 && isMobile && (
                <Divider
                  sx={{
                    width: '90%',
                    borderColor: 'primary.neutral200',
                  }}
                />
              )}
            </Fragment>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
